import CustomTooltip from '../../../../shared/CustomTooltip';
import AddressAutocomplete from '../../../../shared/AddressAutocomplete';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { ServiceArea } from './schema';
import { Contractor } from './ServiceAreas';
import { Address } from '../../../../types/generic';
import {
    contractorStateTitle,
    CONTRACTOR_STATE_LONG_LABEL,
    type ContractorAuState,
} from './contractorStateLabels';
import { contractorStatePlacesAutocompleteOptions } from './contractorStatePlacesBounds';
import { ServiceAreaRegionSummary } from './ServiceAreaRegionSummary';
import LocationPinIcon from '@mui/icons-material/LocationOn';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
    Delete,
    ExpandMore,
    HelpOutline,
    MoreVert,
    PinDrop,
} from '@mui/icons-material';

interface ServiceStateProps {
    area: z.infer<typeof ServiceArea>;
    index: number;
    isEditing: boolean;
    compactShell?: boolean;
    /** When set, compact edit mode uses an accordion for step 2. */
    accordionExpanded?: boolean;
    onAccordionExpandedChange?: (expanded: boolean) => void;
    onRemoveState: (index: number) => void;
}

const ServiceState = ({
    area,
    index,
    isEditing,
    compactShell = false,
    accordionExpanded = false,
    onAccordionExpandedChange,
    onRemoveState,
}: ServiceStateProps) => {
    const theme = useTheme();
    const { control, setValue } = useFormContext<Contractor>();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `serviceStates.${index}.areas`,
    });

    const [areasOpen, setAreasOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const placesAutocompleteOptions = useMemo(
        () =>
            contractorStatePlacesAutocompleteOptions(
                area.state as ContractorAuState,
            ),
        [area.state],
    );

    const handleAddress = (address: Address) => {
        if (!address.placeId || !address.name) return;

        const placeState = (address.state || '').trim().toUpperCase();
        const rowState = area.state.trim().toUpperCase();
        if (placeState !== rowState) return;

        append({
            name: address.name,
            googlePlaceId: address.placeId,
        });
    };

    const handleDelete = (areaIndex: number) => {
        remove(areaIndex);
    };

    const handleAreas = () => {
        if (areasOpen) {
            setValue(`serviceStates.${index}.areas`, [], { shouldDirty: true });
        }

        setAreasOpen(!areasOpen);
    };

    const setCoverageMode = (mode: 'entire' | 'regions') => {
        if (mode === 'entire') {
            setValue(`serviceStates.${index}.areas`, [], { shouldDirty: true });
            setAreasOpen(false);
        } else {
            setAreasOpen(true);
        }
    };

    useEffect(() => {
        if (fields?.length > 0) setAreasOpen(true);
    }, [fields]);

    const stateTitle = contractorStateTitle(area.state);
    const shortStateName =
        CONTRACTOR_STATE_LONG_LABEL[area.state] ?? area.state;
    const hasRegionsFromData = (area.areas?.length ?? 0) > 0;
    const displayBadge = !isEditing
        ? hasRegionsFromData
            ? 'Specific regions'
            : 'Entire state'
        : fields.length > 0 || areasOpen
          ? 'Specific regions'
          : 'Entire state';
    const displayBadgeEntireStyle = !isEditing
        ? !hasRegionsFromData
        : !areasOpen && fields.length === 0;

    const badgeSx = {
        flexShrink: 0,
        px: 1.25,
        py: 0.375,
        borderRadius: 3,
        typography: 'body2' as const,
        fontWeight: 600,
        letterSpacing: 0.01,
        fontSize: '10px',
        ...(displayBadgeEntireStyle
            ? {
                  color: theme.palette.yellow.main,
                  bgcolor: lighten(theme.palette.yellow.main, 0.78),
                  //   border: `1px solid ${lighten(theme.palette.yellow.main, 0.35)}`,
              }
            : {
                  color: 'text.primary',
                  bgcolor: alpha(theme.palette.text.primary, 0.08),
              }),
    };

    const stateRowInner = (
        <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            spacing={1}
            sx={{ width: '100%', pr: isEditing ? 0 : 0 }}
        >
            <Stack
                direction='row'
                alignItems='center'
                spacing={1.5}
                useFlexGap
                sx={{
                    minWidth: 0,
                    flex: 1,
                    flexWrap: 'wrap',
                    rowGap: 1,
                }}
            >
                <Typography
                    variant='body2'
                    sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        lineHeight: 1.35,
                        minWidth: 0,
                    }}
                >
                    {stateTitle}
                </Typography>
                <Box component='span' sx={badgeSx}>
                    {displayBadge}
                </Box>
            </Stack>
            {isEditing ? (
                <>
                    <IconButton
                        type='button'
                        size='small'
                        aria-label='State actions'
                        onClick={(e) => {
                            e.stopPropagation();
                            setMenuAnchor(e.currentTarget);
                        }}
                        sx={{
                            color: 'text.secondary',
                            flexShrink: 0,
                            mr: -0.5,
                        }}
                    >
                        <MoreVert fontSize='small' />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem
                            onClick={() => {
                                setMenuAnchor(null);
                                onRemoveState(index);
                            }}
                            sx={{
                                color: 'error.main',
                                fontWeight: 600,
                                fontSize: '10px',
                            }}
                        >
                            Remove state
                        </MenuItem>
                    </Menu>
                </>
            ) : null}
        </Stack>
    );

    if (compactShell && isEditing && onAccordionExpandedChange) {
        const regionsMode = areasOpen || fields.length > 0;
        const accentMain = theme.palette.textAccent.main;
        const coverageRadioSx = {
            size: 'small' as const,
            sx: {
                color: alpha(theme.palette.text.primary, 0.32),
                '&.Mui-checked': {
                    color: accentMain,
                },
                p: 0.5,
            },
        };

        return (
            <Accordion
                expanded={accordionExpanded}
                onChange={(_, expanded) => onAccordionExpandedChange(expanded)}
                disableGutters
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    mb: 1.5,
                    '&:before': { display: 'none' },
                    '&:last-of-type': { mb: 0 },
                }}
            >
                <AccordionSummary
                    expandIcon={
                        <ExpandMore
                            sx={{ color: 'text.secondary', fontSize: 22 }}
                        />
                    }
                    sx={{
                        px: 2,
                        py: 1.25,
                        minHeight: 48,
                        '& .MuiAccordionSummary-content': {
                            my: 0.5,
                            margin: 0,
                        },
                    }}
                >
                    {stateRowInner}
                </AccordionSummary>
                <AccordionDetails
                    sx={{ px: 2, pb: 2, pt: 0, borderTop: 'none' }}
                >
                    <Stack spacing={2}>
                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 700, color: 'text.primary' }}
                        >
                            How do you service this area?
                        </Typography>

                        <RadioGroup
                            value={regionsMode ? 'regions' : 'entire'}
                            onChange={(_, v) =>
                                setCoverageMode(v as 'entire' | 'regions')
                            }
                            sx={{ width: '100%' }}
                        >
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={6}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.75,
                                            borderRadius: 2,
                                            border: !regionsMode
                                                ? `2px solid ${accentMain}`
                                                : `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                                            bgcolor: !regionsMode
                                                ? theme.palette.textAccent
                                                      .contrastText
                                                : 'background.paper',
                                            height: '100%',
                                        }}
                                    >
                                        <FormControlLabel
                                            value='entire'
                                            control={
                                                <Radio {...coverageRadioSx} />
                                            }
                                            label={
                                                <Stack
                                                    direction='row'
                                                    alignItems='center'
                                                    justifyContent='space-between'
                                                    sx={{
                                                        width: '100%',
                                                        minWidth: 0,
                                                        pl: 0,
                                                        pr: 0,
                                                        gap: 1.25,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            pr: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: !regionsMode
                                                                    ? accentMain
                                                                    : 'text.primary',
                                                                lineHeight: 1.35,
                                                            }}
                                                        >
                                                            Entire state
                                                        </Typography>
                                                        <Typography
                                                            variant='caption'
                                                            sx={{
                                                                display:
                                                                    'block',
                                                                mt: 0.5,
                                                                letterSpacing: 0.02,
                                                                fontWeight: 400,
                                                                color: !regionsMode
                                                                    ? alpha(
                                                                          accentMain,
                                                                          0.88,
                                                                      )
                                                                    : alpha(
                                                                          theme
                                                                              .palette
                                                                              .text
                                                                              .primary,
                                                                          0.72,
                                                                      ),
                                                                lineHeight: 1.45,
                                                            }}
                                                        >
                                                            You service the
                                                            whole {area.state}.
                                                        </Typography>
                                                    </Box>
                                                    <TravelExploreIcon
                                                        sx={{
                                                            fontSize: 28,
                                                            flexShrink: 0,
                                                            color: !regionsMode
                                                                ? accentMain
                                                                : alpha(
                                                                      theme
                                                                          .palette
                                                                          .text
                                                                          .primary,
                                                                      0.5,
                                                                  ),
                                                        }}
                                                    />
                                                </Stack>
                                            }
                                            sx={{
                                                m: 0,
                                                ml: 0,
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                '& .MuiFormControlLabel-label':
                                                    {
                                                        flex: 1,
                                                        minWidth: 0,
                                                    },
                                            }}
                                        />
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.75,
                                            borderRadius: 2,
                                            border: regionsMode
                                                ? `2px solid ${accentMain}`
                                                : `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                                            bgcolor: regionsMode
                                                ? theme.palette.textAccent
                                                      .contrastText
                                                : 'background.paper',
                                            height: '100%',
                                        }}
                                    >
                                        <FormControlLabel
                                            value='regions'
                                            control={
                                                <Radio {...coverageRadioSx} />
                                            }
                                            label={
                                                <Stack
                                                    direction='row'
                                                    alignItems='center'
                                                    justifyContent='space-between'
                                                    sx={{
                                                        width: '100%',
                                                        minWidth: 0,
                                                        pl: 0,
                                                        pr: 0,
                                                        gap: 1.25,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            pr: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: regionsMode
                                                                    ? accentMain
                                                                    : 'text.primary',
                                                                lineHeight: 1.35,
                                                            }}
                                                        >
                                                            Specific regions
                                                        </Typography>
                                                        <Typography
                                                            variant='caption'
                                                            sx={{
                                                                display:
                                                                    'block',
                                                                mt: 0.5,
                                                                letterSpacing: 0.02,
                                                                fontWeight: 400,
                                                                color: regionsMode
                                                                    ? alpha(
                                                                          accentMain,
                                                                          0.88,
                                                                      )
                                                                    : alpha(
                                                                          theme
                                                                              .palette
                                                                              .text
                                                                              .primary,
                                                                          0.72,
                                                                      ),
                                                                lineHeight: 1.45,
                                                            }}
                                                        >
                                                            Choose the regions
                                                            or postcodes you
                                                            travel to.
                                                        </Typography>
                                                    </Box>
                                                    <LocationPinIcon
                                                        sx={{
                                                            fontSize: 28,
                                                            flexShrink: 0,
                                                            color: regionsMode
                                                                ? accentMain
                                                                : alpha(
                                                                      theme
                                                                          .palette
                                                                          .text
                                                                          .primary,
                                                                      0.5,
                                                                  ),
                                                        }}
                                                    />
                                                </Stack>
                                            }
                                            sx={{
                                                m: 0,
                                                ml: 0,
                                                width: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                '& .MuiFormControlLabel-label':
                                                    {
                                                        flex: 1,
                                                        minWidth: 0,
                                                    },
                                            }}
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        </RadioGroup>

                        {regionsMode ? (
                            <Stack spacing={1.5}>
                                <AddressAutocomplete
                                    onChange={handleAddress}
                                    options={placesAutocompleteOptions}
                                    placeholder='Search suburbs, regions or postcodes'
                                    clearOnSelect
                                    fullWidth
                                />
                                {fields.length > 0 ? (
                                    <Stack
                                        direction='row'
                                        flexWrap='wrap'
                                        gap={1}
                                        useFlexGap
                                    >
                                        {fields.map((field, areaIndex) => (
                                            <Chip
                                                key={field.id}
                                                label={field.name}
                                                onDelete={() =>
                                                    handleDelete(areaIndex)
                                                }
                                                size='small'
                                                sx={{ fontWeight: 600 }}
                                            />
                                        ))}
                                    </Stack>
                                ) : null}
                            </Stack>
                        ) : null}

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                pt: 0.5,
                            }}
                        >
                            <Button
                                type='button'
                                variant='text'
                                color='error'
                                size='small'
                                startIcon={<Delete fontSize='small' />}
                                onClick={() => onRemoveState(index)}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Remove {shortStateName}
                            </Button>
                        </Box>
                    </Stack>
                </AccordionDetails>
            </Accordion>
        );
    }

    if (compactShell && !isEditing) {
        const hasRegions = hasRegionsFromData;
        const areaNames = (area.areas ?? [])
            .map((a) => a.name)
            .filter((n): n is string => Boolean(n));
        const summaryLine = hasRegions
            ? areaNames.join(', ') || 'Specific regions selected.'
            : 'You service the whole state.';

        return (
            <Paper
                variant='outlined'
                sx={{
                    borderRadius: 2,
                    p: 1.75,
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Stack
                    direction='row'
                    spacing={1.5}
                    alignItems='center'
                    sx={{ width: '100%', minWidth: 0 }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ...(!hasRegions
                                ? {
                                      bgcolor: lighten(
                                          theme.palette.yellow.main,
                                          0.82,
                                      ),
                                  }
                                : {
                                      bgcolor: alpha(
                                          theme.palette.text.primary,
                                          0.06,
                                      ),
                                  }),
                        }}
                    >
                        {!hasRegions ? (
                            <TravelExploreIcon
                                sx={{
                                    fontSize: 22,
                                    color: theme.palette.yellow.main,
                                }}
                            />
                        ) : (
                            <LocationPinIcon
                                sx={{
                                    fontSize: 22,
                                    color: 'text.secondary',
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant='body2'
                            fontWeight={700}
                            color='text.primary'
                            sx={{ lineHeight: 1.35 }}
                        >
                            {stateTitle}
                        </Typography>
                        <ServiceAreaRegionSummary
                            text={summaryLine}
                            areaNames={hasRegions ? areaNames : undefined}
                            enableClamp={hasRegions}
                        />
                    </Box>
                </Stack>
            </Paper>
        );
    }

    return (
        <Card variant='outlined' sx={{ mb: 1 }}>
            <Stack spacing={1} sx={{ p: 2 }}>
                <div>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                color: 'text.primary',
                            }}
                        >
                            <PinDrop /> {area.state}
                        </Typography>
                        {isEditing ? (
                            <Button
                                type='button'
                                variant='outlined'
                                color='error'
                                size='small'
                                onClick={() => onRemoveState(index)}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    flexShrink: 0,
                                }}
                            >
                                Remove State
                            </Button>
                        ) : null}
                    </Box>
                    {isEditing && (
                        <>
                            <FormControlLabel
                                label='Service entire state?'
                                control={<Checkbox />}
                                sx={{ mr: 1 }}
                                onChange={handleAreas}
                                checked={!areasOpen}
                            />
                            <CustomTooltip
                                title={
                                    <Typography>
                                        Specify which areas within the state
                                        your company services.
                                    </Typography>
                                }
                            >
                                <HelpOutline
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: '16px',
                                    }}
                                />
                            </CustomTooltip>
                            {areasOpen && (
                                <AddressAutocomplete
                                    onChange={handleAddress}
                                    options={placesAutocompleteOptions}
                                    placeholder='Specify which areas your company services'
                                    clearOnSelect
                                />
                            )}
                        </>
                    )}
                </div>
                <div>
                    <List>
                        {fields.length > 0 && (
                            <Typography variant='overline'>
                                Service Areas
                            </Typography>
                        )}
                        {fields.map((field, areaIndex) => (
                            <ListItem key={field.id} divider>
                                <ListItemText primary={field.name} />
                                {isEditing && (
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            color='error'
                                            disabled={!isEditing}
                                            onClick={() =>
                                                handleDelete(areaIndex)
                                            }
                                        >
                                            <Delete />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                )}
                            </ListItem>
                        ))}
                    </List>
                </div>
            </Stack>
        </Card>
    );
};

export default ServiceState;
